import { useState, useRef, type ChangeEvent } from 'react';
import { pageContent } from '@/lib/store';
import type { PageContent } from '@/lib/store';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Save, Image, Search, ChevronDown, Upload } from 'lucide-react';

const pages = [
  { id: 'home', label: 'Homepage' },
  { id: 'about', label: 'About Us' },
  { id: 'services_dlp', label: 'Service - DLP Chip' },
  { id: 'services_power', label: 'Service - Power & Heating' },
  { id: 'services_display', label: 'Service - Display Discolouration' },
  { id: 'services_laser', label: 'Service - Laser & Lamp' },
  { id: 'repair_enquiry', label: 'Repair Enquiry' },
  { id: 'warranty', label: 'Warranty & Returns' },
  { id: 'terms', label: 'Terms & Conditions' },
  { id: 'privacy', label: 'Privacy Policy' },
];

// Per-page image slots
const pageImages: Record<string, { id: string; label: string; currentSrc?: string }[]> = {
  home: [
    { id: 'hero_bg', label: 'Hero Background', currentSrc: '/images/hero-bg-video.mp4' },
    { id: 'service_dlp', label: 'DLP Service Card', currentSrc: '/images/dmd-chip.jpg' },
    { id: 'service_power', label: 'Power Service Card', currentSrc: '/images/power-supply.jpg' },
    { id: 'service_display', label: 'Display Service Card', currentSrc: '/images/display-discolouration.jpg' },
  ],
  about: [
    { id: 'workshop', label: 'Workshop Photo', currentSrc: '/images/projector-repair-workshop.jpg' },
    { id: 'tools', label: 'Tools Photo', currentSrc: '/images/tech-tools.jpg' },
  ],
  services_dlp: [
    { id: 'dlp_detail', label: 'DMD Chip Detail', currentSrc: '/images/dmd-chip.jpg' },
  ],
  services_power: [
    { id: 'power_detail', label: 'Power Supply Detail', currentSrc: '/images/power-supply.jpg' },
  ],
  services_display: [
    { id: 'display_detail', label: 'Optics Detail', currentSrc: '/images/display-discolouration.jpg' },
  ],
};

export default function ContentSEO() {
  const [selectedPage, setSelectedPage] = useState(pages[0].id);
  const [seo, setSeo] = useState<PageContent>(() => pageContent.get(pages[0].id));
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'seo' | 'images'>('seo');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedImageSlot, setSelectedImageSlot] = useState<string | null>(null);

  const loadPage = (pageId: string) => {
    setSelectedPage(pageId);
    setSeo(pageContent.get(pageId));
    setDropdownOpen(false);
  };

  const handleSaveSEO = () => {
    const updated = { ...seo, lastEdited: new Date().toISOString() };
    pageContent.set(selectedPage, updated);
    setSeo(updated);
    toast.success('SEO settings saved');
  };

  const updateSEO = (field: keyof PageContent, value: string) => {
    setSeo(prev => ({ ...prev, [field]: value }));
  };

  const handleImageUpload = (slotId: string) => {
    setSelectedImageSlot(slotId);
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && selectedImageSlot) {
      toast.success(`Image "${file.name}" selected for upload (simulated)`);
    }
    setSelectedImageSlot(null);
  };

  const currentPage = pages.find(p => p.id === selectedPage);
  const images = pageImages[selectedPage] || [];

  // SEO Preview
  const seoPreviewTitle = seo.title || currentPage?.label || 'Page Title';
  const seoPreviewUrl = `https://projectorrepairlondon.co.uk/${selectedPage.replace('_', '/')}`;
  const seoPreviewDesc = seo.description || 'Enter a description to see the preview here.';

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h1 className="font-heading text-2xl font-bold text-white">Content & SEO</h1>
        <p className="text-white/60 text-sm mt-1">Manage SEO settings and images for each page.</p>
      </div>

      {/* Page Selector */}
      <div className="relative">
        <button
          onClick={() => setDropdownOpen(!dropdownOpen)}
          className="flex items-center justify-between w-full sm:w-80 h-11 px-4 bg-[#3d3834] border border-white/10 rounded-lg text-white text-sm hover:border-white/20 transition-colors"
        >
          <span className="flex items-center gap-2">
            <Search className="w-4 h-4 text-[#FFDD00]" />
            {currentPage?.label}
          </span>
          <ChevronDown className={`w-4 h-4 text-white/60 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
        </button>
        {dropdownOpen && (
          <div className="absolute top-full left-0 mt-1 w-full sm:w-80 bg-[#3d3834] border border-white/10 rounded-lg shadow-xl z-10 max-h-72 overflow-auto">
            {pages.map((page) => (
              <button
                key={page.id}
                onClick={() => loadPage(page.id)}
                className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${
                  selectedPage === page.id ? 'bg-[#FFDD00]/10 text-[#0057B7]' : 'text-white/70 hover:text-white hover:bg-white/5'
                }`}
              >
                {page.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-[#3d3834] rounded-lg p-1 w-fit">
        {(['seo', 'images'] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === tab ? 'bg-[#FFDD00] text-[#0057B7]' : 'text-white/70 hover:text-white'
            }`}
          >
            {tab === 'seo' ? 'SEO Settings' : 'Images'}
          </button>
        ))}
      </div>

      {activeTab === 'seo' ? (
        <>
          {/* Google Preview */}
          <div className="card-dark p-5">
            <h3 className="text-xs uppercase tracking-wider text-white/60 mb-3">Google Search Preview</h3>
            <div className="space-y-1">
              <p className="text-[#8AB4F8] text-lg truncate">{seoPreviewTitle} | Wells Electronics Ltd</p>
              <p className="text-[#9AA0A6] text-xs">{seoPreviewUrl}</p>
              <p className="text-[#BDC1C6] text-sm line-clamp-2">{seoPreviewDesc}</p>
            </div>
          </div>

          {/* SEO Form */}
          <div className="card-dark p-6 space-y-5">
            <div className="space-y-2">
              <Label className="text-white/80 text-sm flex justify-between">
                <span>Meta Title</span>
                <span className={`text-xs ${seo.title.length > 60 ? 'text-red-400' : 'text-white/70'}`}>
                  {seo.title.length}/60
                </span>
              </Label>
              <Input
                value={seo.title}
                onChange={(e) => updateSEO('title', e.target.value)}
                placeholder="Page title (max 60 chars)"
                maxLength={60}
                className="h-11 bg-[#0057B7] border-white/10 text-white"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-white/80 text-sm flex justify-between">
                <span>Meta Description</span>
                <span className={`text-xs ${seo.description.length > 160 ? 'text-red-400' : 'text-white/70'}`}>
                  {seo.description.length}/160
                </span>
              </Label>
              <Textarea
                value={seo.description}
                onChange={(e) => updateSEO('description', e.target.value)}
                placeholder="Page description (max 160 chars)"
                maxLength={160}
                className="min-h-[80px] bg-[#0057B7] border-white/10 text-white"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-white/80 text-sm">Keywords</Label>
              <Input
                value={seo.keywords}
                onChange={(e) => updateSEO('keywords', e.target.value)}
                placeholder="Comma-separated keywords"
                className="h-11 bg-[#0057B7] border-white/10 text-white"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-white/80 text-sm">OG Title</Label>
              <Input
                value={seo.ogTitle}
                onChange={(e) => updateSEO('ogTitle', e.target.value)}
                placeholder="Open Graph title"
                className="h-11 bg-[#0057B7] border-white/10 text-white"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-white/80 text-sm">OG Description</Label>
              <Textarea
                value={seo.ogDescription}
                onChange={(e) => updateSEO('ogDescription', e.target.value)}
                placeholder="Open Graph description"
                className="min-h-[80px] bg-[#0057B7] border-white/10 text-white"
              />
            </div>

            <Button onClick={handleSaveSEO} className="btn-primary">
              <Save className="w-4 h-4" />
              Save SEO Settings
            </Button>
          </div>
        </>
      ) : (
        <>
          {/* Images Tab */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleFileChange}
          />

          {images.length === 0 ? (
            <div className="card-dark p-12 text-center">
              <Image className="w-10 h-10 text-white/20 mx-auto mb-3" />
              <p className="text-white/60 text-sm">No image slots configured for this page.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {images.map((img) => (
                <div key={img.id} className="card-dark p-4">
                  <div className="aspect-video bg-[#0057B7] rounded-lg overflow-hidden mb-3">
                    {img.currentSrc ? (
                      <img
                        src={img.currentSrc}
                        alt={img.label}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-white/20">
                        <Image className="w-8 h-8" />
                      </div>
                    )}
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-white">{img.label}</p>
                      <p className="text-xs text-white/60 truncate max-w-[200px]">{img.currentSrc || 'No image'}</p>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleImageUpload(img.id)}
                      className="border-white/10 text-white/60 hover:text-white hover:bg-white/5"
                    >
                      <Upload className="w-3.5 h-3.5 mr-1" />
                      Change
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}